/**
 * Single source of truth for reading an order item's service status in the booking app.
 *
 * `ServiceStatus` is the ambient type generated from the API's `App\Enums\ServiceStatus`
 * (`apps/shared/types/generated.d.ts`, regenerated by `npm run generate-types` and verified in CI),
 * so a status added on the backend surfaces here as a type error rather than as silently wrong copy.
 * It is a global declaration — referenced as `App.Enums.ServiceStatus`, never imported.
 *
 * Why this file exists: `BOOKED` was introduced as the terminal booked status in March 2026 and no
 * consumer in this app was updated. Eight separate hardcoded `['READY', 'COMPLETED']` arrays meant a
 * fully booked packing service rendered "Pending approval" to the customer for five months.
 */
export type ServiceStatus = App.Enums.ServiceStatus;

/**
 * The service has been delivered and its money has settled — safe to describe as paid.
 */
const SETTLED_STATUSES = new Set<string>(['READY', 'COMPLETED']);

/**
 * The service is confirmed and scheduled, but payment is a separate concern and may still be
 * outstanding — so these must not be described as paid.
 *
 * `ACCEPTED` is the legacy spelling of `BOOKED` and is still present in live data; see the comment
 * on `TransitionServiceStatus::canTransition()`, which keeps accepting it for exactly this reason.
 */
const BOOKED_STATUSES = new Set<string>(['BOOKED', 'ACCEPTED']);

function normalise(status?: string | null): string | null {
    return status ? status.toUpperCase().replace(/\s+/g, '_') : null;
}

export function isSettledServiceStatus(status?: string | null): boolean {
    const normalised = normalise(status);

    return normalised !== null && SETTLED_STATUSES.has(normalised);
}

export function isBookedServiceStatus(status?: string | null): boolean {
    const normalised = normalise(status);

    return normalised !== null && BOOKED_STATUSES.has(normalised);
}

/**
 * Customer-facing status labels. Mirrors `serviceStatusOptions` in the CRM
 * (`apps/crm/src/types/orderItems/OrderItem.ts`) so the two apps name a status identically.
 *
 * Typed as a total map over `ServiceStatus`, so adding a case to the backend enum fails the build
 * here until it is given a label.
 */
export const SERVICE_STATUS_LABELS: Record<ServiceStatus, string> = {
    NEW: 'New',
    DRAFT: 'Draft',
    QUOTED: 'Quoted',
    PENDING: 'Pending',
    BOOKED: 'Booked',
    ACCEPTED: 'Accepted',
    CANCELLED: 'Cancelled',
    DECLINED: 'Declined',
    COMPLETED: 'Completed',
    REFUNDED: 'Refunded',
    READY: 'Ready',
};

/**
 * Human label for a status, or null when there is nothing meaningful to show — callers should hide
 * the chip rather than print a raw enum value.
 */
export function serviceStatusLabel(status?: string | null): string | null {
    const normalised = normalise(status);

    if (!normalised) {
        return null;
    }

    return SERVICE_STATUS_LABELS[normalised as ServiceStatus] ?? null;
}
